import axios, {AxiosError, AxiosHeaders} from 'axios'
import {fetch as tauriFetch} from '@tauri-apps/plugin-http'
import {POS_API_ORIGIN} from './tauri'

const cookieJar = new Map()

const resolveRequestUrl = (config) => {
  const baseUrl = config.baseURL || POS_API_ORIGIN
  const url = new URL(axios.getUri({...config, baseURL: baseUrl}))

  if (url.origin !== POS_API_ORIGIN) {
    throw new AxiosError('Tauri API transport rejected an unapproved host', AxiosError.ERR_BAD_REQUEST, config)
  }

  return url.toString()
}

const requestHeaders = (config) => {
  const headers = AxiosHeaders.from(config.headers).toJSON()
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    Object.keys(headers).forEach((name) => {
      if (name.toLowerCase() === 'content-type') delete headers[name]
    })
  }
  const cookies = [...cookieJar.entries()].map(([name, value]) => `${name}=${value}`).join('; ')
  if (cookies) headers.Cookie = cookies
  return headers
}

const updateCookies = (headers) => {
  const values = typeof headers.getSetCookie === 'function'
    ? headers.getSetCookie()
    : [headers.get('set-cookie')].filter(Boolean)

  values.forEach((header) => {
    const [pair, ...attributes] = header.split(';')
    const separator = pair.indexOf('=')
    if (separator < 1) return

    const name = pair.slice(0, separator).trim()
    const value = pair.slice(separator + 1).trim()
    const expired = attributes.some((attribute) => /^max-age\s*=\s*0$/i.test(attribute.trim()))
    if (!value || expired) cookieJar.delete(name)
    else cookieJar.set(name, value)
  })
}

const responseData = async (response, responseType) => {
  if (responseType === 'arraybuffer') return response.arrayBuffer()
  if (responseType === 'blob') return response.blob()
  if (responseType === 'text') return response.text()

  const text = await response.text()
  if (!text) return ''
  const contentType = response.headers.get('content-type') || ''
  if (responseType === 'json' || contentType.includes('json')) {
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }
  return text
}

const settle = (response, config) => {
  const validateStatus = config.validateStatus
  if (!response.status || !validateStatus || validateStatus(response.status)) return Promise.resolve(response)

  const code = response.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST
  return Promise.reject(new AxiosError(
    `Request failed with status code ${response.status}`,
    code,
    config,
    null,
    response,
  ))
}

export const createTauriHttpAdapter = ({fetchImpl = tauriFetch} = {}) => async (config) => {
  const url = resolveRequestUrl(config)
  const response = await fetchImpl(url, {
    method: (config.method || 'get').toUpperCase(),
    headers: requestHeaders(config),
    body: ['GET', 'HEAD'].includes((config.method || 'get').toUpperCase()) ? undefined : config.data,
    signal: config.signal,
    connectTimeout: config.timeout || 30_000,
    maxRedirections: 0,
  })

  updateCookies(response.headers)
  const axiosResponse = {
    data: await responseData(response, config.responseType),
    status: response.status,
    statusText: response.statusText,
    headers: AxiosHeaders.from(Object.fromEntries(response.headers.entries())),
    config,
    request: null,
  }

  return settle(axiosResponse, config)
}

export const clearTauriSessionCookies = () => cookieJar.clear()
